import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setLoading, setError, setRoom } from '../../store/slices/roomSlice';
import api from '../../utils/api';
import { clearRoom } from '../../store/slices/roomSlice';
import { clearMedia } from '../../store/slices/mediaSlice';
import { RECORDING_UI_ENABLED } from '../../config/meetingFeatureFlags';
import {
  ensureMeetingSessionId,
  getOrCreateMeetingDeviceId,
  hydrateMeetingIdentity,
} from '../../utils/meetingAuth';


const CreateRoom = () => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [lobby, setLobby] = useState(true);
  const [screenSharing, setScreenSharing] = useState(true);
  const [recording, setRecording] = useState(true);
  const [automaticRecordingEnabled, setAutomaticRecordingEnabled] = useState(false);
  const [chat, setChat] = useState(true);

  const { loading, error } = useSelector((state) => state.room);
  const dispatch = useDispatch();
  const navigate = useNavigate();
useEffect(() => {
    Object.keys(localStorage).forEach((k) => {
      if (k.startsWith('meet-lock:')) localStorage.removeItem(k);
    });
  ;
  }, []);
  const handleSubmit = async (e) => {
    e.preventDefault();
  
    // ✅ STEP 1: CLEAR OLD ROOM/MEDIA STATE
    dispatch(clearRoom());
    dispatch(clearMedia());
  
    dispatch(setLoading(true));
  
    // ✅ STEP 2: Generate sessionId
hydrateMeetingIdentity();
const sessionId = ensureMeetingSessionId();
const deviceSig = getOrCreateMeetingDeviceId();
sessionStorage.setItem('deviceSig', deviceSig);
localStorage.setItem('deviceSig', deviceSig);
sessionStorage.setItem('deviceId', deviceSig);
localStorage.setItem('deviceId', deviceSig);
    try {
      // ✅ STEP 3: Create the room via API
      const response = await api.post('/rooms', {
        title,
        description,
        settings: {
          lobby,
          screenSharing,
          recording,
          automaticRecordingEnabled,
          chat,
        },
        deviceId:deviceSig,
        sessionId,
      });
  if(response.data)
    
    sessionStorage.setItem('isHost', true);
    sessionStorage.setItem('statusget', 'admitted');
    localStorage.setItem('roomId', response.data.roomId);
      dispatch(setRoom(response.data));
  
      // ✅ STEP 4: Navigate to MeetingRoom with fresh state
      navigate(`/room/${response.data.roomId}`, { state: response.data });
  
    } catch (err) {
      dispatch(setError(err.response?.data?.message || 'Failed to create room'));
    }
  };
  

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <h1 className="text-2xl font-bold mb-6">Create a New Meeting</h1>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
            Meeting Title *
          </label>
          <input
            id="title"
            type="text"
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="mb-4">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            id="description"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Meeting Settings</h3>

          <div className="space-y-3">
            {/* Lobby toggle */}
            <div className="flex items-center">
              <input
                id="lobby"
                type="checkbox"
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                checked={lobby}
                onChange={(e) => setLobby(e.target.checked)}
              />
              <label htmlFor="lobby" className="ml-2 block text-sm text-gray-700">
                Enable waiting room (participants need to be admitted)
              </label>
            </div>

            {/* Screen sharing toggle */}
            <div className="flex items-center">
              <input
                id="screenSharing"
                type="checkbox"
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                checked={screenSharing}
                onChange={(e) => setScreenSharing(e.target.checked)}
              />
              <label htmlFor="screenSharing" className="ml-2 block text-sm text-gray-700">
                Allow screen sharing
              </label>
            </div>

            {/* Recording setting stays in the submitted room payload, but its UI is hidden. */}
            {RECORDING_UI_ENABLED && (
              <div className="flex items-center">
                <input
                  id="recording"
                  type="checkbox"
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  checked={recording}
                  onChange={(e) => setRecording(e.target.checked)}
                />
                <label htmlFor="recording" className="ml-2 block text-sm text-gray-700">
                  Allow recording (host only)
                </label>
              </div>
            )}

            <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4">
              <label htmlFor="automaticRecordingEnabled" className="flex items-start gap-3">
                <input id="automaticRecordingEnabled" type="checkbox" className="mt-1 h-4 w-4"
                  checked={automaticRecordingEnabled}
                  onChange={(event) => { setAutomaticRecordingEnabled(event.target.checked); if (event.target.checked) setRecording(true); }} />
                <span><strong className="block text-sm text-indigo-950">Automatically record this meeting</strong>
                  <span className="block text-xs leading-5 text-indigo-800">Starts when an authorized Admin, Staff or Faculty host joins. Recordings are saved privately. Admin and Staff can pause this requirement and enable it again.</span>
                </span>
              </label>
            </div>

            {/* Chat toggle */}
            <div className="flex items-center">
              <input
                id="chat"
                type="checkbox"
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                checked={chat}
                onChange={(e) => setChat(e.target.checked)}
              />
              <label htmlFor="chat" className="ml-2 block text-sm text-gray-700">
                Enable chat
              </label>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading || !title}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating...' : 'Create Meeting'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateRoom;
